import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import styled, { createGlobalStyle, keyframes, css } from "styled-components";
import { format, formatDistanceToNow } from "date-fns";
import { parseDate } from "../utils/helpers/date";
import {
  User,
  MessageCircle,
  DollarSign,
  Calendar,
  Briefcase,
  MapPin,
  Star,
  PlusCircle,
  Image as ImageIcon,
  Clock,
  Search,
  Navigation,
  Target,
  ArrowUpDown,
  Settings,
  Filter,
  ChevronDown,
  ChevronRight,
  X,
  Menu,
  SlidersHorizontal,
  Bell,
  LogIn,
} from "lucide-react";

import { request } from "../services/networking/request";
import { API_CONSTANT_MAP } from "../utils/constants/endpoints";
import Loader from "../components/Loading";
import NavBar from "../components/layout/NavBar";
import BackButton from "../components/BackButton";
import SafetyTip from "../components/safety/SafetyTip";
import VerificationBadge from "../components/common/VerificationBadge";
import ImageModal from "../components/ImageModal";

/* =============== DESIGN TOKENS =============== */
const theme = {
  colors: {
    primary: "#3b82f6",
    primaryHover: "#2563eb",
    primaryLight: "#eff6ff",
    secondary: "#6366f1",
    textPrimary: "#0f172a",
    textSecondary: "#475569",
    textMuted: "#64748b",
    surface: "#ffffff",
    surfaceHover: "#f8fafc",
    background: "#ffffff",
    border: "#e2e8f0",
    borderLight: "#f1f5f9",
    accent: "#10b981",
    warning: "#f59e0b",
    error: "#ef4444",
  },
  gradients: {
    primary: "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)",
    secondary: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    surface: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
  },
  shadows: {
    xs: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    sm: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
    md: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
    lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
    navbar: "0 1px 3px 0 rgb(0 0 0 / 0.05), 0 1px 2px -1px rgb(0 0 0 / 0.05)",
  },
  spacing: {
    xs: "0.25rem",
    sm: "0.5rem",
    md: "0.75rem",
    lg: "1rem",
    xl: "1.5rem",
    xxl: "2rem",
  },
  borderRadius: {
    sm: "0.375rem",
    md: "0.5rem",
    lg: "0.75rem",
    xl: "1rem",
    full: "9999px",
  },
  typography: {
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
  },
};

/* =============== GLOBAL/ANIM =============== */
const fadeIn = keyframes`from{opacity:0}to{opacity:1}`;
const slideInUp = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}`;
const GlobalStyle = createGlobalStyle` 
  body { 
    background: ${theme.colors.background}; 
    font-family: ${theme.typography.fontFamily}; 
    color: ${theme.colors.textPrimary};
    font-weight: ${theme.typography.fontWeight.normal};
  }
`;

/* =============== LAYOUT/COMMON =============== */
const Page = styled.div`
  min-height: 100vh;
  padding-top: 64px;
  background: ${theme.gradients.surface};
  font-family: ${theme.typography.fontFamily};
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: ${theme.spacing.xl};

  @media (max-width: 1024px) {
    padding: 0;
    width: 100%;
    max-width: 100vw;
    overflow-x: hidden;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: ${theme.spacing.xxl};

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 0;
  }
`;

/* ========================================= */
/* ===== REDESIGNED FILTER SECTION START ===== */
/* ========================================= */

const SearchBarContainer = styled.div`
  margin-bottom: ${theme.spacing.lg};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.lg};
  flex-wrap: wrap;
  position: relative;
  z-index: 1050;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: ${theme.spacing.md};
    margin-bottom: ${theme.spacing.md};
  }
`;

const SearchBar = styled.div`
  position: relative;
  flex: 1;
  min-width: 320px;
  background: ${theme.colors.surface};
  border-radius: 2rem;
  border: 1px solid ${theme.colors.border};
  box-shadow: ${theme.shadows.sm};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    border-color: ${theme.colors.textMuted};
    box-shadow: ${theme.shadows.md};
  }

  &:focus-within {
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primaryLight};
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    min-width: auto;
    width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 3.5rem 0.75rem 3rem;
  border: none;
  border-radius: 2rem;
  font-size: 0.875rem;
  font-weight: ${theme.typography.fontWeight.medium};
  font-family: ${theme.typography.fontFamily};
  background: transparent;
  color: ${theme.colors.textPrimary};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.textMuted};
  pointer-events: none;
  transition: color 0.2s ease;

  ${SearchBar}:focus-within & {
    color: ${theme.colors.primary};
  }
`;

const SearchButton = styled.button`
  position: absolute;
  right: 0.25rem;
  top: 50%;
  transform: translateY(-50%);
  background: ${theme.gradients.primary};
  color: white !important;
  border: none;
  border-radius: 1.5rem;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-weight: ${theme.typography.fontWeight.semibold};
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);

  &:hover {
    transform: translateY(-50%) scale(1.05);
    box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
    color: white !important;
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
    color: white !important;
  }
`;

const FilterPillsRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  flex-wrap: wrap;
  padding-top: ${theme.spacing.xs};

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
    gap: ${theme.spacing.xs};
  }
`;

const FilterPill = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.lg};
  font-size: 0.8125rem;
  font-weight: ${theme.typography.fontWeight.semibold};
  font-family: ${theme.typography.fontFamily};
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.surface};
  box-shadow: ${theme.shadows.xs};
  color: ${theme.colors.textSecondary};

  &:hover {
    border-color: ${theme.colors.textMuted};
    background: ${theme.colors.surfaceHover};
    box-shadow: ${theme.shadows.sm};
    transform: translateY(-1px);
    color: ${theme.colors.textPrimary};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px ${theme.colors.primaryLight}, ${theme.shadows.sm};
  }

  &:active {
    transform: translateY(0px);
  }

  ${(props) =>
    props.filled
      ? `
    background: ${theme.gradients.primary};
    border-color: ${theme.colors.primary};
    color: white !important;
    font-weight: ${theme.typography.fontWeight.bold};
    box-shadow: ${theme.shadows.sm};

    &:hover {
      background: ${theme.colors.primaryHover};
      box-shadow: ${theme.shadows.md};
      transform: translateY(-2px);
      color: white !important;
    }

    &:active {
      color: white !important;
    }
  `
      : props.danger
      ? `
    background: hsl(0, 100%, 98%);
    border-color: hsl(0, 84%, 85%);
    color: hsl(0, 84%, 44%) !important;
    font-weight: ${theme.typography.fontWeight.semibold};

    &:hover {
      background: hsl(0, 100%, 95%);
      border-color: hsl(0, 84%, 75%);
      color: hsl(0, 84%, 40%) !important;
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0px);
      color: hsl(0, 84%, 40%) !important;
    }
  `
      : `
    color: ${theme.colors.textSecondary};

    &:hover {
      color: ${theme.colors.textPrimary};
    }
  `}
`;

const ResultsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 4px;
`;

const ResultsCount = styled.div`
  font-size: 14px;
  color: #5e6670;
  font-weight: 400;
`;

const ClearButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.full};
  background: #fef2f2;
  color: ${theme.colors.error};
  font-size: 0.8125rem;
  font-weight: ${theme.typography.fontWeight.semibold};
  font-family: ${theme.typography.fontFamily};
  border: 1px solid #fecaca;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${theme.shadows.xs};

  &:hover {
    background: #fee2e2;
    border-color: #fca5a5;
    box-shadow: ${theme.shadows.sm};
    transform: translateY(-0.5px);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.15), ${theme.shadows.sm};
  }

  &:active {
    transform: translateY(0px);
  }
`;

// UPDATED: Location Dropdown with fixed positioning to prevent clipping
const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.lg};
  z-index: 10000;
  margin-top: ${theme.spacing.sm};
  min-width: 280px;
  transform-origin: top;
  animation: ${slideInUp} 0.2s ease;
`;

const DropdownContent = styled.div`
  padding: ${theme.spacing.xl};
`;

const DropdownLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.textSecondary};
  margin-bottom: ${theme.spacing.md};
`;

const DropdownInput = styled.input`
  width: 100%;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: 0.875rem;
  background: ${theme.colors.surface};
  color: ${theme.colors.textPrimary};
  font-family: ${theme.typography.fontFamily};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    background: ${theme.colors.surface};
    box-shadow: 0 0 0 3px ${theme.colors.primaryLight};
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }

  &:hover {
    border-color: ${theme.colors.borderHover};
  }
`;

const BudgetInputRow = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  align-items: center;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-width: 0;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${theme.spacing.sm};
    align-items: stretch;

    span {
      align-self: flex-start;
      font-size: 0.75rem;
      color: ${theme.colors.textMuted};
      margin: 0;
    }
  }

  @media (max-width: 480px) {
    gap: ${theme.spacing.xs};
  }

  span {
    color: ${theme.colors.textSecondary};
    font-size: 0.875rem;
    font-weight: ${theme.typography.fontWeight.medium};
    text-align: center;
    flex-shrink: 0;
    min-width: 20px;
  }
`;

const DropdownActions = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: flex-end;
  margin-top: ${theme.spacing.xl};
  padding-top: ${theme.spacing.lg};
  border-top: 1px solid ${theme.colors.border};
`;

const DropdownButton = styled.button`
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.full};
  font-size: 0.875rem;
  font-weight: ${theme.typography.fontWeight.semibold};
  font-family: ${theme.typography.fontFamily};
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;

  ${(props) =>
    props.primary
      ? `
    background: ${theme.gradients.primary};
    color: white !important;
    box-shadow: ${theme.shadows.sm};

    &:hover {
      background: ${theme.colors.primaryHover};
      transform: translateY(-1px);
      box-shadow: ${theme.shadows.md};
      color: white !important;
    }

    &:active {
      transform: translateY(0);
      color: white !important;
    }
  `
      : `
    background: ${theme.colors.surfaceHover};
    color: ${theme.colors.textSecondary};
    border: 1px solid ${theme.colors.border};

    &:hover {
      background: ${theme.colors.border};
      color: ${theme.colors.textPrimary};
    }
  `}
`;

const DropdownOption = styled.div`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  cursor: pointer;
  font-size: 0.875rem;
  color: ${theme.colors.textSecondary};
  border-radius: ${theme.borderRadius.md};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: ${theme.colors.surfaceHover};
    color: ${theme.colors.textPrimary};
  }

  &:active {
    background: ${theme.colors.border};
  }
`;

const MobileFilterButton = styled.button`
  display: none;

  @media (max-width: 1024px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: calc(100% - 32px);
    max-width: calc(100vw - 32px);
    padding: 14px 18px;
    margin: 0 16px 16px;
    background: white;
    border: 1px solid #e8ecf4;
    border-radius: 12px;
    color: ${theme.colors.textBody};
    font-size: 16px;
    font-weight: 600;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
    transition: all 0.2s ease;
    cursor: pointer;
    line-height: 1.2;
    min-height: 52px;
    box-sizing: border-box;

    &:hover {
      background: #f8fafc;
      border-color: ${theme.colors.primary};
      box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0px);
      box-shadow: 0 2px 6px rgba(15, 23, 42, 0.06);
    }

    .filter-content {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .filter-badge {
      background: ${theme.colors.primaryLight};
      color: ${theme.colors.primary};
      font-size: 12px;
      font-weight: 700;
      padding: 4px 8px;
      border-radius: 8px;
      min-width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    svg {
      flex-shrink: 0;
      display: block;
      color: ${theme.colors.primary};
    }

    span:first-of-type {
      font-weight: 600;
      font-size: 16px;
      line-height: 1.2;
      color: inherit;
    }
  }
`;

const MobileHeader = styled.div`
  display: none;

  @media (max-width: 1024px) {
    display: flex;
    align-items: center;
    gap: 8px;
    width: calc(100% - 24px);
    max-width: calc(100vw - 24px);
    margin: 0 12px 12px;
    box-sizing: border-box;
    padding-right: 8px; /* Extra space for filter badge */
  }
`;

const MobileSearchContainer = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
`;

const MobileSearchInput = styled.input`
  width: 100%;
  height: 44px;
  padding: 12px 45px 12px 16px;
  background: #f1f5f9;
  border: none;
  border-radius: 22px;
  color: ${theme.colors.textPrimary};
  font-size: 15px;
  font-weight: 400;
  transition: all 0.2s ease;
  line-height: 1.2;
  box-sizing: border-box;
  outline: none;

  &::placeholder {
    color: ${theme.colors.textMuted};
    font-weight: 400;
  }

  &:focus {
    background: white;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
  }

  &:hover:not(:focus) {
    background: #e2e8f0;
  }
`;

const MobileSearchIcon = styled.button`
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${theme.colors.textPrimary};
  z-index: 1;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(15, 23, 42, 0.05);
  }

  svg {
    display: block;
  }
`;

const MobileFilterIcon = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: white;
  border: 1px solid ${theme.colors.border};
  border-radius: 10px;
  color: ${theme.colors.textPrimary};
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;
  margin-left: 8px;
  overflow: visible; /* Allow badge to extend outside */
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  &:hover {
    background: ${theme.colors.surfaceHover};
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }

  .filter-badge {
    position: absolute;
    top: -2px;
    right: -2px;
    background: ${theme.colors.primary};
    color: white;
    font-size: 10px;
    font-weight: 700;
    padding: 2px 5px;
    border-radius: 8px;
    min-width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
    z-index: 10;
  }

  svg {
    flex-shrink: 0;
    color: ${theme.colors.textPrimary};
  }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.4);
  z-index: 9998;
`;
const MobileSheet = styled.div`
  position: fixed;
  inset: 0;
  background: linear-gradient(135deg, #ffffff 0%, #fafbff 100%);
  z-index: 400010;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  max-width: 100vw;
  min-width: 0;
  overflow: hidden;
  box-sizing: border-box;
`;

const MobileSheetHeader = styled.div`
  padding: 24px 20px 16px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #fafbff 0%, #ffffff 100%);
  position: relative;

  h2 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 800;
    color: ${theme.colors.textPrimary};
    display: flex;
    align-items: center;
    gap: 12px;

    svg {
      color: ${theme.colors.primary};
    }
  }

  .close-button {
    background: #f1f5f9;
    border: none;
    border-radius: 12px;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    color: ${theme.colors.textMuted};

    &:hover {
      background: #e2e8f0;
      color: ${theme.colors.textBody};
    }

    &:active {
      transform: scale(0.95);
    }
  }
`;

const MobileSheetContent = styled.div`
  padding: 16px 16px 100px 16px;
  overflow-y: auto;
  flex: 1;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-width: 0;

  @media (max-width: 375px) {
    padding: 12px 12px 100px 12px;
  }
`;

const MobileSheetFooter = styled.div`
  padding: 20px;
  border-top: 1px solid #f1f5f9;
  background: linear-gradient(135deg, #fafbff 0%, #ffffff 100%);
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-top: 20px;
`;

const MobileFilterForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-width: 0;

  @media (max-width: 375px) {
    gap: 14px;
  }
`;

const MobileFilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-width: 0;

  &:last-child {
    margin-bottom: 0;
  }
`;

const MobileFilterLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
`;

const MobileFilterInput = styled.input`
  width: 100%;
  max-width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 16px;
  font-size: 14px;
  background: white;
  color: #374151;
  transition: all 0.2s ease;
  box-sizing: border-box;
  min-width: 0;
  flex: 1;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  &:hover {
    border-color: #9ca3af;
  }

  &::placeholder {
    color: #9ca3af;
    font-size: 13px;
  }

  @media (max-width: 375px) {
    padding: 8px 10px;
    font-size: 13px;

    &::placeholder {
      font-size: 12px;
    }
  }
`;

const MobileFilterSelect = styled.select`
  width: 100%;
  max-width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 16px;
  font-size: 14px;
  background: white;
  color: #374151;
  transition: all 0.2s ease;
  box-sizing: border-box;
  min-width: 0;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  &:hover {
    border-color: #9ca3af;
  }

  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 14px;
  appearance: none;

  @media (max-width: 375px) {
    padding: 8px 10px;
    font-size: 13px;
    background-position: right 8px center;
    background-size: 12px;
  }
`;

const MobileButton = styled.button`
  flex: 1;
  padding: 14px 16px;
  border-radius: 16px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  min-height: 48px;
  position: relative;
  overflow: hidden;

  ${(props) =>
    props.primary
      ? `
    background: linear-gradient(135deg, ${theme.colors.primary} 0%, #6366f1 100%);
    color: white;
    box-shadow: 0 4px 16px rgba(59, 130, 246, 0.3);

    &:hover {
      background: linear-gradient(135deg, #2563eb 0%, #6366f1 100%);
      box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0px);
      box-shadow: 0 3px 12px rgba(59, 130, 246, 0.4);
    }
  `
      : `
    background: #f1f5f9;
    color: #64748b;
    border: 1px solid #e2e8f0;

    &:hover {
      background: #e2e8f0;
      color: #475569;
      border-color: #cbd5e1;
    }

    &:active {
      background: #cbd5e1;
      transform: scale(0.98);
    }
  `}
`;

/* ========================================= */
/* ====== OLD FILTER COMPONENTS (removed) ====== */
/* ========================================= */

// Old components are removed from the code for clarity and to prevent duplication.
// `FilterSummaryBar`, `Chip`, `FilterToggle`, `HeaderCard`, `FilterForm`, `MainRow`, etc.
// are no longer part of the updated code.

/* ========================================= */
/* ===== REDESIGNED FILTER SECTION END ===== */
/* ========================================= */

/* =============== SIDEBAR/DETAILS (unchanged layout) =============== */
const Sidebar = styled.aside`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.xl};
  box-shadow: ${theme.shadows.sm};
  overflow: hidden;
  height: calc(100vh - 64px - 24px - 18px);
  display: flex;
  flex-direction: column;
  @media (max-width: 1024px) {
    border-radius: 0;
    border-left: 0;
    border-right: 0;
    height: calc(100vh - 64px - 56px);
    display: ${(p) => (p.selectedTask ? "none" : "flex")};
  }
`;
const ListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px dashed ${theme.colors.border};
  background: #fbfdff;
`;
const List = styled.div`
  background: linear-gradient(180deg, #fbfdff, #fff);
  padding: 14px;
  flex: 1;
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 10px;
  }
  > * + * {
    margin-top: 12px;
  }
`;
const Item = styled.button`
  width: 100%;
  text-align: left;
  border: 1px solid ${theme.colors.borderLight};
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  cursor: pointer;
  position: relative;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${theme.shadows.sm};
  margin-bottom: ${theme.spacing.md};
  font-family: ${theme.typography.fontFamily};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
    background: ${theme.colors.surfaceHover};
    border-color: ${theme.colors.border};
  }

  &:active {
    transform: translateY(0);
    background: ${theme.colors.primaryLight};
  }

  @media (max-width: 768px) {
    border: 2px solid ${theme.colors.borderLight};
    box-shadow: ${theme.shadows.md};

    &:hover {
      transform: none; /* Disable hover transform on mobile */
    }

    &:active {
      transform: scale(0.98);
      border-color: ${theme.colors.primary};
    }
  }

  ${(p) =>
    p.selected &&
    css`
      border-color: ${theme.colors.primary};
      box-shadow: 0 4px 16px rgba(59, 130, 246, 0.15);
      background: ${theme.colors.primaryLight};
    `}
`;
const ItemTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
`;

const ItemRightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MobileChevron = styled.div`
  display: none;
  color: ${theme.colors.textMuted};
  opacity: 0.7;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
  }
`;

const ItemHeader = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemTitle = styled.div`
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.textPrimary};
  font-size: 1.05rem;
  font-family: ${theme.typography.fontFamily};
  letter-spacing: -0.015em;
  line-height: 1.4;
  word-break: break-word;
  margin-bottom: ${theme.spacing.xs};
`;

const ItemSubtitle = styled.div`
  font-size: 0.85rem;
  color: ${theme.colors.textMuted};
  font-weight: ${theme.typography.fontWeight.medium};
  font-family: ${theme.typography.fontFamily};
`;

const Price = styled.div`
  font-weight: ${theme.typography.fontWeight.extrabold};
  font-size: 1.125rem;
  font-family: ${theme.typography.fontFamily};
  color: ${theme.colors.textSecondary};
  flex-shrink: 0;
  text-align: right;
  letter-spacing: -0.015em;
`;

const ItemRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 6px;
  background: #f8fafc;
  font-weight: 500;
  font-size: 0.75rem;
  color: #64748b;

  svg {
    width: 12px;
    height: 12px;
  }
`;

const Details = styled.main`
  background: ${theme.colors.surface};
  border: 1px solid #f1f5f9;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04),
    0 1px 3px rgba(15, 23, 42, 0.06);
  height: calc(100vh - 64px - 24px - 18px);
  padding: 0;
  overflow-y: auto;
  animation: ${fadeIn} 0.25s ease;
  position: relative;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 10px;
  }

  @media (max-width: 1024px) {
    display: ${(p) => (p.selectedTask ? "block" : "none")};
    border-radius: 0;
    border-left: 0;
    border-right: 0;
    height: calc(100vh - 64px - 56px);
  }
`;
const StickyHead = styled.div`
  position: sticky;
  top: 0;
  background: linear-gradient(135deg, #ffffff 0%, #fafbff 100%);
  backdrop-filter: saturate(180%) blur(20px);
  border-bottom: 1px solid #f1f5f9;
  margin-bottom: 32px;
  padding: 24px 32px;
  z-index: 10;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);

  a {
    text-decoration: none;
    color: #0f172a;
    font-size: 1.5rem;
    font-weight: 800;
    letter-spacing: -0.025em;
    display: block;
    line-height: 1.3;
    margin-bottom: 16px;
    transition: color 0.2s ease;
  }

  a:hover {
    color: ${theme.colors.primary};
  }

  @media (max-width: 1024px) {
    background: ${theme.colors.surface};
    backdrop-filter: none;
    border-bottom: 1px solid ${theme.colors.border};
    padding: 20px 16px;
    margin-bottom: 24px;

    a {
      font-size: 1.25rem;
      margin-bottom: 12px;
    }
  }

  @media (max-width: 640px) {
    padding: 16px 14px;
    margin-bottom: 20px;

    a {
      font-size: 1.1rem;
      margin-bottom: 10px;
    }
  }
`;
const ContentArea = styled.div`
  padding: 0 32px 40px;
  @media (max-width: 1024px) {
    padding: 0 16px 24px;
  }
`;
const MobileBack = styled.button`
  display: none;
  @media (max-width: 1024px) {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #fff;
    border: 1px solid ${theme.colors.border};
    color: ${theme.colors.textBody};
    padding: 8px 10px;
    border-radius: 999px;
    font-weight: 700;
    cursor: pointer;
  }
`;
const PosterRow = styled.div`
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${theme.colors.textMuted};
  font-size: 0.92rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const PosterInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 8px;
  }
`;

const ManageTaskButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  color: ${theme.colors.textBody};
  border: 1px solid ${theme.colors.border};
  border-radius: 10px;
  padding: 8px 10px;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  margin-left: auto;
  transition: border 0.12s, background 0.12s;
  &:hover {
    background: ${theme.colors.primaryLight};
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primaryText};
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 10px 12px;
    margin-left: 0;
  }
`;
const Avatar = styled.div`
  width: ${(props) => props.size || "32px"};
  height: ${(props) => props.size || "32px"};
  border-radius: 50%;
  overflow: hidden;
  background: ${(props) => props.bgColor || theme.colors.primaryLight};
  color: white;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: ${(props) => props.fontSize || "14px"};
  flex-shrink: 0;
  line-height: 1;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;
const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin: 32px 0;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
`;
const SoftStat = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fbfdff;
  border: 1px dashed ${theme.colors.border};
  border-radius: 12px;
  padding: 12px 14px;

  .label {
    color: ${theme.colors.textMuted};
    font-weight: 600;
    font-size: 0.9rem;
  }
  .value {
    color: ${theme.colors.textBody};
    font-weight: 700;
    font-size: 0.96rem;
  }
`;
const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin: 20px 0 16px;
`;
const Primary = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${theme.gradients.primary};
  color: #fff;
  border: 0;
  border-radius: 10px;
  padding: 8px 10px;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(59, 130, 246, 0.25);
  transition: transform 0.1s, box-shadow 0.1s;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(59, 130, 246, 0.28);
  }
`;
const Ghost = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  color: ${theme.colors.textBody};
  border: 1px solid ${theme.colors.border};
  border-radius: 10px;
  padding: 8px 10px;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  transition: border 0.12s, background 0.12s;
  &:hover {
    background: ${theme.colors.primaryLight};
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primaryText};
  }
`;
const Section = styled.section`
  padding: 20px 0 28px;
  animation: ${slideInUp} 0.25s ease;
  & + & {
    border-top: 1px dashed ${theme.colors.border};
    margin-top: 16px;
    padding-top: 24px;
  }
`;
const SectionTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  margin: 0 0 ${theme.spacing.lg};
  font-size: 1.125rem;
  font-weight: ${theme.typography.fontWeight.bold};
  font-family: ${theme.typography.fontFamily};
  color: ${theme.colors.textPrimary};
  letter-spacing: -0.025em;

  svg {
    color: ${theme.colors.primary};
    background: ${theme.colors.primaryLight};
    border-radius: ${theme.borderRadius.md};
    padding: ${theme.spacing.xs};
    width: 28px;
    height: 28px;
  }
`;
const Body = styled.p`
  color: ${theme.colors.textBody};
  line-height: 1.7;
  margin: 0;
  font-size: 0.95rem;
  letter-spacing: -0.01em;
`;
const TagWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;
const TimePill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(
    135deg,
    ${theme.colors.primaryLight} 0%,
    #f0f7ff 100%
  );
  border: 1px solid rgba(59, 130, 246, 0.2);
  color: ${theme.colors.primaryText};
  padding: 8px 14px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.85rem;
  letter-spacing: -0.01em;
  transition: all 0.2s ease;

  &:hover {
    background: linear-gradient(135deg, #e0f2fe 0%, #eff6ff 100%);
    border-color: ${theme.colors.primary};
    transform: translateY(-1px);
  }
`;
const Thumb = styled.img`
  height: 84px;
  width: 128px;
  object-fit: cover;
  border-radius: 10px;
  border: 1px solid ${theme.colors.border};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-color: ${theme.colors.primary};
  }
`;
const Skeleton = styled.div`
  border-radius: 12px;
  background: linear-gradient(90deg, #f1f5f9, #f8fafc, #f1f5f9);
  background-size: 200% 100%;
  animation: shimmer 1.2s infinite linear;
  height: ${(p) => p.h || "14px"};
  @keyframes shimmer {
    from {
      background-position: 200% 0;
    }
    to {
      background-position: -200% 0;
    }
  }
`;
const Empty = styled.div`
  padding: 28px;
  text-align: center;
  color: ${theme.colors.textMuted};
  border: 1px dashed ${theme.colors.border};
  border-radius: 14px;
  background: #fafcff;
`;

const EmptyStateButton = styled.button`
  background: ${theme.gradients.primary};
  color: white !important;
  border: none;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  font-size: 0.875rem;
  font-weight: ${theme.typography.fontWeight.semibold};
  font-family: ${theme.typography.fontFamily};
  cursor: pointer;
  margin: ${theme.spacing.lg} auto 0 auto;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  box-shadow: ${theme.shadows.sm};

  &:hover {
    background: ${theme.colors.primaryHover};
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
    color: white !important;
  }

  &:active {
    transform: translateY(0);
    color: white !important;
  }
`;

const NotificationBanner = styled.div`
  background: linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%);
  border: 1px solid #bfdbfe;
  border-radius: 12px;
  padding: 16px 20px;
  margin: 20px 0;
  display: flex;
  align-items: center;
  gap: 12px;

  .icon {
    flex-shrink: 0;
    color: #3b82f6;
  }

  .content {
    flex: 1;

    .title {
      font-size: 14px;
      font-weight: 600;
      color: #1e40af;
      margin: 0 0 4px 0;
    }

    .description {
      font-size: 13px;
      color: #1d4ed8;
      margin: 0;
      line-height: 1.4;
    }
  }
`;

const NotificationButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;

  &:hover {
    background: #2563eb;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

/* =============== DATA HOOK =============== */
const useBrowseTasksData = (initialPayload = null, userState = null) => {
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [offers, setOffers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOffersLoading, setIsOffersLoading] = useState(false);
  const [isQuestionsLoading, setIsQuestionsLoading] = useState(false);

  const fetchTasks = async (payload = {}) => {
    setIsLoading(true);
    const searchStartTime = Date.now();
    console.log("fetchTasks called with payload:", payload);
    try {
      const { data } = await request(
        "POST",
        API_CONSTANT_MAP.fetch_tasks,
        payload
      );
      if (data?.success) {
        const newTasks = data.tasks || [];
        setTasks(newTasks);
        const isDesktop =
          typeof window !== "undefined" && window.innerWidth > 1024;

        // Always auto-select first task on desktop when there are results
        if (newTasks.length > 0 && isDesktop) {
          setSelectedTask(newTasks[0]);
        } else if (newTasks.length === 0) {
          setSelectedTask(null);
        } else if (!isDesktop && selectedTask) {
          // On mobile, only keep selection if task still exists
          const stillExists = newTasks.find((t) => t.id === selectedTask.id);
          if (!stillExists) setSelectedTask(null);
        }

        // Track search for analytics (only for signed-in users)
        const searchDuration = Date.now() - searchStartTime;
        trackUserSearch(payload, newTasks.length, searchDuration);
      }
    } catch (err) {
      console.error("Failed to fetch tasks:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Track user search behavior for analytics
  const trackUserSearch = async (searchData, resultsCount, durationMs) => {
    try {
      // Only track meaningful searches (not initial page loads with default params)
      const hasSearchQuery = searchData.q && searchData.q.trim().length > 0;
      const hasFilters =
        searchData.task_type !== "all" ||
        searchData.zipcode ||
        searchData.min_budget ||
        searchData.max_budget ||
        searchData.sort_by !== "date_desc";

      if (!hasSearchQuery && !hasFilters) {
        return; // Skip tracking default/initial searches
      }

      await request("POST", API_CONSTANT_MAP.store_user_search, {
        search_data: searchData,
        results_count: resultsCount,
        search_duration_ms: durationMs,
        session_id:
          sessionStorage.getItem("searchSessionId") || generateSessionId(),
      });
    } catch (err) {
      // Silently fail - don't interrupt user experience for analytics
      console.warn("Failed to track search:", err);
    }
  };

  // Generate unique session ID for grouping searches
  const generateSessionId = () => {
    const sessionId = `search_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    sessionStorage.setItem("searchSessionId", sessionId);
    return sessionId;
  };

  useEffect(() => {
    // Use initialPayload if provided, otherwise use default
    const defaultPayload = {
      task_type: "all",
      sort_by: "date_desc",
    };
    const payloadToUse = initialPayload || defaultPayload;

    console.log("Hook initial fetch with payload:", payloadToUse);
    fetchTasks(payloadToUse);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedTask) return;

    // Fetch offers
    setIsOffersLoading(true);
    request("POST", API_CONSTANT_MAP.fetch_task_offers, {
      task_id: selectedTask.id,
    })
      .then(({ data }) => setOffers(data?.success ? data.offers : []))
      .catch((err) => console.error("Error fetching offers:", err))
      .finally(() => setIsOffersLoading(false));

    // Fetch questions
    setIsQuestionsLoading(true);
    request("POST", API_CONSTANT_MAP.fetch_task_questions, {
      task_id: selectedTask.id,
    })
      .then(({ data }) => setQuestions(data?.success ? data.questions : []))
      .catch((err) => console.error("Error fetching questions:", err))
      .finally(() => setIsQuestionsLoading(false));
  }, [selectedTask]);

  const addNewOffer = (newOffer) => setOffers((prev) => [newOffer, ...prev]);
  const addNewQuestion = (newQuestion) =>
    setQuestions((prev) => [newQuestion, ...prev]);

  return {
    tasks,
    selectedTask,
    setSelectedTask,
    offers,
    questions,
    addNewOffer,
    addNewQuestion,
    isLoading,
    isOffersLoading,
    isQuestionsLoading,
    fetchTasks,
  };
};

/* =============== OFFER FORM =============== */
const OfferFormCard = styled.div`
  background: ${theme.colors.surface};
  border-radius: 16px;
  border: 1px solid ${theme.colors.border};
  padding: 24px;
  box-shadow: ${theme.colors.sm};
  animation: ${slideInUp} 0.25s ease;
`;
const FormField = styled.div`
  margin-bottom: 18px;
  label {
    display: block;
    font-size: 0.95rem;
    font-weight: 600;
    margin-bottom: 8px;
    color: ${theme.colors.textBody};

    /* Style required field asterisks */
    & > span.required {
      color: #dc2626;
      font-weight: 700;
      margin-left: 2px;
    }
  }
`;
const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid
    ${(props) => (props.$hasError ? "#ef4444" : theme.colors.border)};
  background: #fff;
  transition: all 0.2s ease;

  ${(props) =>
    props.$hasError &&
    css`
      animation: shake 0.5s ease-in-out;
    `}

  &:focus {
    outline: none;
    border-color: ${(props) =>
      props.$hasError ? "#ef4444" : theme.colors.primary};
    box-shadow: ${(props) =>
      props.$hasError ? "none" : `0 0 0 4px ${theme.colors.primaryLight}`};
  }

  @keyframes shake {
    0%,
    100% {
      transform: translateX(0);
    }
    25% {
      transform: translateX(-2px);
    }
    75% {
      transform: translateX(2px);
    }
  }
`;
const Textarea = styled(Input).attrs({ as: "textarea" })`
  resize: vertical;
  min-height: 110px;
`;
const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;
const ExperienceTag = styled.div`
  display: inline-block;
  background: ${theme.colors.primaryLight};
  color: ${theme.colors.primaryText};
  padding: 5px 8px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.78rem;
  border: 1px solid rgba(59, 130, 246, 0.2);
  margin-top: 8px;
`;
const OfferCard = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #fafbff 100%);
  border: 1px solid #f1f5f9;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  animation: ${slideInUp} 0.2s ease;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  transition: all 0.3s ease;

  &:hover {
    border-color: ${theme.colors.primary};
    box-shadow: 0 8px 32px rgba(15, 23, 42, 0.12);
    transform: translateY(-2px);
  }
`;
const OfferHeader = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 12px;
`;

const OfferPrice = styled.div`
  margin-left: auto;
  font-weight: 800;
  font-size: 1.25rem;
  color: ${theme.colors.textSecondary};
  padding: 8px 14px;
  border-radius: 10px;
  letter-spacing: -0.02em;
`;

const OfferUserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const OfferUserName = styled.div`
  font-weight: 700;
  font-size: 1rem;
  color: ${theme.colors.textPrimary};
  margin-bottom: 2px;
`;

const OfferTimestamp = styled.div`
  font-size: 0.85rem;
  color: ${theme.colors.textMuted};
`;

const OfferProposal = styled.div`
  color: ${theme.colors.textBody};
  line-height: 1.6;
  margin: 0 0 16px;
  font-size: 0.95rem;
`;

const OfferSection = styled.div`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const OfferSectionTitle = styled.div`
  font-weight: 700;
  font-size: 0.9rem;
  color: ${theme.colors.textPrimary};
  margin-bottom: 6px;
`;

const OfferSectionContent = styled.div`
  font-size: 0.92rem;
  color: ${theme.colors.textMuted};
  line-height: 1.5;
`;

const OfferMeta = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f1f5f9;
`;

const OfferMetaItem = styled.div`
  .label {
    color: ${theme.colors.textMuted};
    font-weight: 600;
    font-size: 0.85rem;
    margin-bottom: 4px;
  }

  .value {
    font-weight: 700;
    color: ${theme.colors.textBody};
    font-size: 0.9rem;
  }
`;

const QuestionCard = styled(OfferCard)``;

const QuestionHeader = styled(OfferHeader)``;

const QuestionUserInfo = styled(OfferUserInfo)``;

const QuestionUserName = styled(OfferUserName)``;

/* =============== FLOATING ACTION BUTTON =============== */
const FloatingActionButton = styled.button`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${theme.gradients.primary};
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: ${theme.shadows.lg};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
  font-size: 0;

  &:hover {
    transform: translateY(-4px) scale(1.05);
    box-shadow: 0 20px 40px rgba(59, 130, 246, 0.3);
  }

  &:active {
    transform: translateY(-2px) scale(1.02);
  }

  @media (max-width: 768px) {
    bottom: 90px; /* Above mobile nav if present */
    width: 56px;
    height: 56px;
  }

  @media (max-width: 480px) {
    bottom: 80px;
    right: 20px;
    width: 52px;
    height: 52px;
  }
`;

const FloatingButtonIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;

  ${FloatingActionButton}:hover & {
    transform: rotate(90deg);
  }
`;

const QuestionTimestamp = styled(OfferTimestamp)``;

const QuestionText = styled.div`
  color: ${theme.colors.textBody};
  line-height: 1.6;
  margin: 0 0 16px;
  font-size: 0.95rem;
`;

const QuestionIcon = styled.div`
  margin-left: auto;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    ${theme.colors.primaryLight} 0%,
    #e0f2fe 100%
  );
  border: 1px solid #c7d2fe;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.primary};
`;

const RepliesContainer = styled.div`
  margin-top: 16px;
  padding-left: 20px;
  border-left: 3px solid ${theme.colors.primaryLight};
  background: linear-gradient(135deg, #fafbff 0%, #f8fafc 100%);
  border-radius: 0 12px 12px 0;
  padding: 16px 20px;
`;

const ReplyItem = styled.div`
  margin-bottom: 12px;
  padding: 0;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ReplyHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

const ReplyUserName = styled.div`
  font-weight: 600;
  font-size: 0.85rem;
  color: ${theme.colors.textPrimary};
`;

const ReplyTimestamp = styled.div`
  font-size: 0.75rem;
  color: ${theme.colors.textMuted};
`;

const ReplyText = styled.div`
  font-size: 0.9rem;
  color: ${theme.colors.textBody};
  padding-left: 32px;
  line-height: 1.5;
`;

const SecondaryButton = Ghost;
const PrimaryButton = Primary;

const QuestionFormComponent = ({ task, onCancel, onQuestionSubmitted }) => {
  const [questionText, setQuestionText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleSubmit = async () => {
    if (!questionText.trim()) {
      setHasError(true);
      return;
    }
    setHasError(false);
    setIsSubmitting(true);
    try {
      const payload = {
        task_id: task.id,
        question_text: questionText.trim(),
      };
      const { data } = await request(
        "POST",
        API_CONSTANT_MAP.submit_task_question,
        payload
      );
      if (data?.success) {
        onQuestionSubmitted(data.question);
        onCancel();
      } else {
        console.error(
          "Question submission error:",
          data.error?.message || "Unknown error"
        );
      }
    } catch (error) {
      console.error("Failed to submit question:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <OfferFormCard>
      <SectionTitle>Ask a Question</SectionTitle>
      <FormField>
        <Textarea
          value={questionText}
          onChange={(e) => {
            setQuestionText(e.target.value);
            if (hasError) setHasError(false);
          }}
          placeholder="What would you like to know about this task?"
          style={{ minHeight: "120px" }}
          $hasError={hasError}
        />
      </FormField>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 10,
          marginTop: 10,
        }}
      >
        <SecondaryButton type="button" onClick={onCancel}>
          Cancel
        </SecondaryButton>
        <PrimaryButton
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Submitting..." : "Submit Question"}
        </PrimaryButton>
      </div>
    </OfferFormCard>
  );
};

const OfferFormComponent = ({ task, onCancel, onOfferSubmitted }) => {
  const [formData, setFormData] = useState({
    rate: "",
    proposal: "",
    duration: "",
    completionDate: "",
    yearsExperience: "",
    previousWork: "",
    portfolioLinks: [""],
    clientQuestions: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));

    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  const handlePortfolioLinkChange = (i, v) => {
    const links = [...formData.portfolioLinks];
    links[i] = v;
    setFormData((p) => ({ ...p, portfolioLinks: links }));
  };
  const addPortfolioLink = () =>
    setFormData((p) => ({ ...p, portfolioLinks: [...p.portfolioLinks, ""] }));

  const handleSubmit = async () => {
    // Validate required fields
    const errors = {};
    if (!formData.rate || formData.rate.trim() === "") {
      errors.rate = true;
    }
    if (!formData.proposal || formData.proposal.trim() === "") {
      errors.proposal = true;
    }

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      setSubmitError("Budget and Proposal are required.");
      return;
    }
    setSubmitError("");
    setIsSubmitting(true);
    try {
      const payload = {
        task_id: task.id,
        offer_amount: formData.rate,
        proposal: formData.proposal,
        duration: formData.duration,
        completion_date: formData.completionDate,
        years_experience: formData.yearsExperience,
        previous_similar_work: formData.previousWork,
        portfolio_links: formData.portfolioLinks.filter((x) => x.trim()),
        client_questions: formData.clientQuestions,
      };
      const { data } = await request(
        "POST",
        API_CONSTANT_MAP.create_task_offer,
        payload
      );
      if (data?.success) {
        onOfferSubmitted(data.offer);
        onCancel();
      } else {
        setSubmitError(
          data.error?.message || "Failed to submit offer. Please try again."
        );
        console.error(
          "Offer submission error:",
          data.error?.message || "Unknown error"
        );
      }
    } catch (error) {
      setSubmitError("Failed to submit offer. Please try again.");
      console.error("Failed to submit offer:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <OfferFormCard>
      <SectionTitle>Your Proposal</SectionTitle>

      {/* Safety Tip for Making Offers */}
      <SafetyTip
        tip={{
          id: "safe-offering",
          title: "Stay Safe While Making Offers",
          content:
            "Read the task details carefully and ask questions if unclear. Never pay upfront or share personal banking information. Keep all communication on the platform.",
          icon: "🛡️",
          priority: "high",
        }}
        type="info"
        compact={true}
      />
      <FormField>
        <label>
          Your Proposed Rate ($) <span className="required">*</span>
        </label>
        <Input
          type="number"
          name="rate"
          value={formData.rate}
          onChange={handleChange}
          placeholder={`Client's budget is $${task.budget}`}
          $hasError={validationErrors.rate}
          required
        />
      </FormField>
      <FormRow>
        <FormField>
          <label>Estimated Duration</label>
          <Input
            name="duration"
            value={formData.duration}
            onChange={handleChange}
            placeholder="e.g. 2 Weeks"
          />
        </FormField>
        <FormField>
          <label>Proposed Completion Date</label>
          <Input
            name="completionDate"
            value={formData.completionDate}
            onChange={handleChange}
            type="date"
          />
        </FormField>
      </FormRow>
      <FormField>
        <label>
          Why are you the best fit? <span className="required">*</span>
        </label>
        <Textarea
          name="proposal"
          value={formData.proposal}
          onChange={handleChange}
          placeholder="Highlight your relevant skills and experience..."
          $hasError={validationErrors.proposal}
          required
        />
      </FormField>
      <FormField>
        <label>Years of Relevant Experience</label>
        <Input
          type="number"
          name="yearsExperience"
          value={formData.yearsExperience}
          onChange={handleChange}
          placeholder="e.g., 5"
        />
      </FormField>
      <FormField>
        <label>Describe Previous Similar Work</label>
        <Textarea
          name="previousWork"
          value={formData.previousWork}
          onChange={handleChange}
          placeholder="Briefly describe 1–2 past projects."
        />
      </FormField>
      <FormField>
        <label>Portfolio / Work Samples</label>
        {formData.portfolioLinks.map((link, i) => (
          <Input
            key={i}
            value={link}
            onChange={(e) => handlePortfolioLinkChange(i, e.target.value)}
            placeholder="https://your-portfolio.com"
            style={{ marginBottom: 8 }}
          />
        ))}
        <button
          onClick={addPortfolioLink}
          type="button"
          style={{
            background: "none",
            border: 0,
            cursor: "pointer",
            color: theme.colors.primaryText,
            fontWeight: 700,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: ".9rem",
          }}
        >
          <PlusCircle size={16} /> Add another link
        </button>
      </FormField>
      <FormField>
        <label>Questions for the Client (optional)</label>
        <Textarea
          name="clientQuestions"
          value={formData.clientQuestions}
          onChange={handleChange}
          placeholder="Anything you need clarified?"
        />
      </FormField>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          marginTop: 20,
          paddingTop: 16,
          borderTop: `1px solid ${theme.colors.border}`,
        }}
      >
        {submitError && (
          <span style={{ color: "#ef4444", fontSize: "14px", flex: 1 }}>
            {submitError}
          </span>
        )}
        <div
          style={{
            display: "flex",
            gap: 12,
            marginLeft: submitError ? "auto" : "0",
          }}
        >
          <SecondaryButton type="button" onClick={onCancel}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit Proposal"}
          </PrimaryButton>
        </div>
      </div>
    </OfferFormCard>
  );
};

/* =============== UTILITY FUNCTIONS =============== */
// Generate consistent brand color for all avatars
const getUserColor = (userId) => {
  return "#3b82f6"; // Brand blue - consistent for all users
};

// Generate user initials
const getUserInitials = (firstName, lastName) => {
  if (!firstName && !lastName) return "A"; // Anonymous

  const first = firstName?.charAt(0)?.toUpperCase() || "";
  const last = lastName?.charAt(0)?.toUpperCase() || "";

  if (first && last) return `${first}${last}`;
  if (first) return first;
  if (last) return last;
  return "A";
};

/* =============== MAIN PAGE =============== */
const BrowseTasksPage = ({ userState }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Filters
  const initialQuery = searchParams.get("query") || searchParams.get("q") || "";
  const initialTaskType = searchParams.get("task_type") || "all";
  const initialCategory = searchParams.get("category") || "";

  const [query, setQuery] = useState(initialQuery);
  const [taskType, setTaskType] = useState(initialTaskType); // all | remote | in-person
  const [zipcode, setZipcode] = useState("");
  const [radius, setRadius] = useState(25); // miles
  const [userLocation, setUserLocation] = useState(null); // {lat, lng} or null
  const [minBudget, setMinBudget] = useState("");
  const [maxBudget, setMaxBudget] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(initialCategory); // category filter
  const [categories, setCategories] = useState([]); // available categories
  const [sortBy, setSortBy] = useState("date_desc"); // date_desc|date_asc|budget_desc|budget_asc

  // Filters UI
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  // LinkedIn-style dropdown states
  const [showBudgetDropdown, setShowBudgetDropdown] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  // Refs for click outside detection
  const budgetDropdownRef = useRef(null);
  const locationDropdownRef = useRef(null);
  const categoryDropdownRef = useRef(null);
  const mobileSheetRef = useRef(null);
  const mobileButtonRef = useRef(null);

  // FIXED: Moved useRef to the top level of the component
  const isInitialMount = useRef(true);

  // Image modal state
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [modalImages, setModalImages] = useState([]);

  // Image modal handlers
  const openImageModal = (images, index = 0) => {
    setModalImages(images);
    setCurrentImageIndex(index);
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
    setModalImages([]);
    setCurrentImageIndex(0);
  };

  const handleNextImage = (newIndex) => {
    setCurrentImageIndex(newIndex);
  };

  const handlePreviousImage = (newIndex) => {
    setCurrentImageIndex(newIndex);
  };

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        budgetDropdownRef.current &&
        !budgetDropdownRef.current.contains(event.target)
      ) {
        setShowBudgetDropdown(false);
      }
      if (
        locationDropdownRef.current &&
        !locationDropdownRef.current.contains(event.target)
      ) {
        setShowLocationDropdown(false);
      }
      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(event.target)
      ) {
        setShowCategoryDropdown(false);
      }
      if (
        mobileSheetRef.current &&
        !mobileSheetRef.current.contains(event.target) &&
        mobileButtonRef.current &&
        !mobileButtonRef.current.contains(event.target)
      ) {
        setIsFiltersOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Tabs + offer form + question form
  const [activeTab, setActiveTab] = useState("offers");
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [showQuestionForm, setShowQuestionForm] = useState(false);

  const hasUrlParams = initialQuery || initialTaskType !== "all";

  // Build initial payload for hook if URL params exist
  const initialPayload = hasUrlParams
    ? {
        q: initialQuery,
        task_type: initialTaskType,
        sort_by: "date_desc",
      }
    : null;

  const {
    tasks,
    selectedTask,
    setSelectedTask,
    offers,
    questions,
    addNewOffer,
    addNewQuestion,
    isLoading,
    isOffersLoading,
    isQuestionsLoading,
    fetchTasks,
  } = useBrowseTasksData(initialPayload, userState);

  // Fetch categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data } = await request(
          "POST",
          API_CONSTANT_MAP.fetch_categories
        );
        if (data?.success) {
          setCategories(data.categories || []);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  // Check for onboarding redirect
  useEffect(() => {
    if (userState?.user?.token && userState.user.onboarding_status === 'pending') {
      const currentPath = window.location.pathname + window.location.search;
      const onboardingUrl = `/onboarding?redirect=${encodeURIComponent(currentPath)}`;
      navigate(onboardingUrl);
    }
  }, [userState?.user?.onboarding_status, userState?.user?.token, navigate]);

  // Initial search when component mounts or URL params change
  useEffect(() => {
    // Small delay to ensure categories are loaded first
    const timer = setTimeout(() => {
      applyFilters();
    }, 100);

    return () => clearTimeout(timer);
  }, []); // Only run once on mount

  useEffect(() => {
    setActiveTab("offers");
    setShowOfferForm(false);
    setShowQuestionForm(false);
  }, [selectedTask?.id]);

  const isMobile = typeof window !== "undefined" && window.innerWidth <= 1024;

  // Build and execute filter payload - only called explicitly
  const applyFilters = (overrides = {}) => {
    const currentTaskType =
      overrides.taskType !== undefined ? overrides.taskType : taskType;

    const payload = {
      q: (overrides.query !== undefined ? overrides.query : query).trim(),
      task_type: currentTaskType,
      // Don't include location data for remote tasks
      zipcode:
        currentTaskType === "online"
          ? ""
          : (overrides.zipcode !== undefined
              ? overrides.zipcode
              : zipcode
            ).trim(),
      radius:
        currentTaskType === "online"
          ? 25
          : overrides.radius !== undefined
          ? overrides.radius
          : radius,
      lat:
        currentTaskType === "online"
          ? null
          : overrides.userLocation !== undefined
          ? overrides.userLocation?.lat ?? null
          : userLocation?.lat ?? null,
      lng:
        currentTaskType === "online"
          ? null
          : overrides.userLocation !== undefined
          ? overrides.userLocation?.lng ?? null
          : userLocation?.lng ?? null,
      min_budget:
        overrides.minBudget !== undefined
          ? overrides.minBudget
            ? Number(overrides.minBudget)
            : null
          : minBudget
          ? Number(minBudget)
          : null,
      max_budget:
        overrides.maxBudget !== undefined
          ? overrides.maxBudget
            ? Number(overrides.maxBudget)
            : null
          : maxBudget
          ? Number(maxBudget)
          : null,
      sort_by: overrides.sortBy !== undefined ? overrides.sortBy : sortBy,
      category_id:
        overrides.selectedCategory !== undefined
          ? overrides.selectedCategory || null
          : selectedCategory || null,
    };
    console.log("🔍 ZIP Filter Debug - Payload being sent:", payload);
    console.log("🔍 ZIP Filter Debug - Current state:", {
      taskType: currentTaskType,
      zipcode,
      userLocation,
      radius,
    });
    fetchTasks(payload);
    if (isMobile) setIsFiltersOpen(false);
  };

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (query.trim()) count++;
    if (taskType !== "all") count++;
    if (minBudget || maxBudget) count++;
    if (zipcode.trim() || userLocation) count++;
    if (selectedCategory) count++;
    if (sortBy !== "date_desc") count++;
    return count;
  }, [
    query,
    taskType,
    minBudget,
    maxBudget,
    zipcode,
    userLocation,
    selectedCategory,
    sortBy,
  ]);

  const [isGettingLocation, setIsGettingLocation] = useState(false);

  const getCurrentLocation = () => {
    // If location is already set, clear it (toggle off)
    if (userLocation) {
      setUserLocation(null);
      applyFilters({ userLocation: null });
      return;
    }

    if (!navigator?.geolocation) {
      console.warn("Geolocation is not supported by your browser.");
      return;
    }

    setIsGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const newLocation = {
          lat: Number(pos.coords.latitude.toFixed(6)),
          lng: Number(pos.coords.longitude.toFixed(6)),
        };
        setUserLocation(newLocation);
        setZipcode("");
        setIsGettingLocation(false);
        applyFilters({ userLocation: newLocation, zipcode: "" });
      },
      (err) => {
        console.warn("Geolocation error:", err);
        setIsGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
  };

  const clearFilters = () => {
    setQuery("");
    setTaskType("all");
    setZipcode("");
    setRadius(25);
    setUserLocation(null);
    setMinBudget("");
    setMaxBudget("");
    setSelectedCategory("");
    setSortBy("date_desc");

    // Explicitly trigger search with cleared filter values
    applyFilters({
      query: "",
      taskType: "all",
      zipcode: "",
      radius: 25,
      userLocation: null,
      minBudget: "",
      maxBudget: "",
      selectedCategory: "",
      sortBy: "date_desc",
    });
  };

  // Computed values
  const hasActiveFilters = !!(
    query.trim() ||
    taskType !== "all" ||
    zipcode.trim() ||
    userLocation ||
    minBudget ||
    maxBudget ||
    selectedCategory ||
    sortBy !== "date_desc"
  );

  const getTaskTypeText = () =>
    taskType === "online"
      ? "Remote"
      : taskType === "in-person"
      ? "In-person"
      : "All Tasks";
  const getSortByText = () =>
    ({
      date_asc: "Oldest first",
      budget_desc: "Highest budget",
      budget_asc: "Lowest budget",
    }[sortBy] || "Most Recent");
  const getLocationText = () =>
    userLocation
      ? `My Location (${radius} mi)`
      : zipcode
      ? `${zipcode} (${radius} mi)`
      : "Anywhere";
  const getBudgetText = () =>
    minBudget && maxBudget
      ? `$${minBudget} - $${maxBudget}`
      : minBudget
      ? `Min $${minBudget}`
      : maxBudget
      ? `Max $${maxBudget}`
      : "Any Budget";

  const getCategoryText = () => {
    if (!selectedCategory) return "All Categories";
    const category = categories.find(
      (cat) => cat.id.toString() === selectedCategory.toString()
    );
    return category ? category.name : "All Categories";
  };

  return (
    <>
      <GlobalStyle />
      <Page>
        <NavBar user={userState.user} />
        <Container>
          {/* Mobile Header - Search + Filter for mobile */}
          <MobileHeader
            style={{
              display: selectedTask ? "none" : undefined,
            }}
          >
            <MobileSearchContainer>
              <MobileSearchInput
                type="text"
                placeholder="Search for anything"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    applyFilters();
                  }
                }}
              />
              <MobileSearchIcon onClick={() => applyFilters()}>
                <Search size={20} />
              </MobileSearchIcon>
            </MobileSearchContainer>
            <MobileFilterIcon onClick={() => setIsFiltersOpen(true)}>
              <SlidersHorizontal size={16} />
              {activeFiltersCount > 0 && (
                <span className="filter-badge">{activeFiltersCount}</span>
              )}
            </MobileFilterIcon>
          </MobileHeader>

          {/* Desktop LinkedIn-style filters */}
          <SearchBarContainer
            style={{
              display: window.innerWidth <= 1024 ? "none" : "flex",
              marginBottom: "16px",
            }}
          >
            <SearchBar>
              <SearchIcon>
                <Search size={20} />
              </SearchIcon>
              <SearchInput
                type="text"
                placeholder="Search tasks..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    applyFilters();
                  }
                }}
              />
              <SearchButton
                onClick={(e) => {
                  e.preventDefault();
                  applyFilters();
                }}
                type="button"
              >
                <Search size={16} />
              </SearchButton>
            </SearchBar>

            <FilterPillsRow>
              {/* Location dropdown */}
              <FilterPill
                ref={locationDropdownRef}
                filled={zipcode || userLocation}
                disabled={taskType === "online"}
                onClick={() => {
                  if (taskType === "online") return;
                  setShowLocationDropdown(!showLocationDropdown);
                  setShowBudgetDropdown(false);
                }}
                style={{
                  opacity: taskType === "online" ? 0.5 : 1,
                  cursor: taskType === "online" ? "not-allowed" : "pointer",
                  pointerEvents: taskType === "online" ? "none" : "auto",
                }}
              >
                {getLocationText()}
                <ChevronDown size={14} />
                {showLocationDropdown && (
                  <DropdownMenu onClick={(e) => e.stopPropagation()}>
                    <DropdownContent>
                      <DropdownLabel>Search near a ZIP code</DropdownLabel>
                      <DropdownInput
                        type="text"
                        placeholder="Enter ZIP code"
                        value={zipcode}
                        onChange={(e) => {
                          setZipcode(e.target.value);
                          if (e.target.value.trim()) setUserLocation(null);
                        }}
                      />
                      <DropdownLabel style={{ marginTop: "16px" }}>
                        Or use your current location
                      </DropdownLabel>
                      <DropdownButton
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          getCurrentLocation();
                        }}
                        style={{
                          width: "100%",
                          marginBottom: "16px",
                          background: userLocation
                            ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                            : undefined,
                          color: userLocation ? "white" : undefined,
                        }}
                      >
                        <Navigation size={16} />{" "}
                        {isGettingLocation
                          ? "Getting Location..."
                          : userLocation
                          ? "Location Set ✓"
                          : "Use My Location"}
                      </DropdownButton>
                      {userLocation && (
                        <div
                          style={{
                            marginBottom: "16px",
                            padding: "10px 12px",
                            background:
                              "linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)",
                            border: "1px solid #a7f3d0",
                            borderRadius: "8px",
                            fontSize: "12px",
                            color: "#065f46",
                            fontWeight: "600",
                            textAlign: "center",
                          }}
                        >
                          📍 {userLocation.lat}, {userLocation.lng}
                        </div>
                      )}
                      <DropdownLabel>Search radius</DropdownLabel>
                      <MobileFilterSelect
                        value={radius}
                        onChange={(e) => setRadius(e.target.value)}
                      >
                        <option value="5">5 miles</option>
                        <option value="10">10 miles</option>
                        <option value="25">25 miles</option>
                        <option value="50">50 miles</option>
                        <option value="100">100 miles</option>
                      </MobileFilterSelect>
                      <DropdownActions>
                        <DropdownButton
                          onClick={(e) => {
                            e.preventDefault();
                            setZipcode("");
                            setUserLocation(null);
                            applyFilters({ zipcode: "", userLocation: null });
                            setShowLocationDropdown(false);
                          }}
                        >
                          Clear
                        </DropdownButton>
                        <DropdownButton
                          primary
                          onClick={(e) => {
                            e.preventDefault();
                            applyFilters();
                            setShowLocationDropdown(false);
                          }}
                        >
                          Apply
                        </DropdownButton>
                      </DropdownActions>
                    </DropdownContent>
                  </DropdownMenu>
                )}
              </FilterPill>

              {/* Budget Filter */}
              <FilterPill
                ref={budgetDropdownRef}
                filled={minBudget || maxBudget}
                onClick={() => {
                  setShowBudgetDropdown(!showBudgetDropdown);
                  setShowLocationDropdown(false);
                }}
              >
                {getBudgetText()}
                <ChevronDown size={14} />
                {showBudgetDropdown && (
                  <DropdownMenu onClick={(e) => e.stopPropagation()}>
                    <DropdownContent>
                      <DropdownLabel>Budget range</DropdownLabel>
                      <BudgetInputRow>
                        <DropdownInput
                          type="number"
                          placeholder="Min"
                          value={minBudget}
                          onChange={(e) => setMinBudget(e.target.value)}
                        />
                        <span>to</span>
                        <DropdownInput
                          type="number"
                          placeholder="Max"
                          value={maxBudget}
                          onChange={(e) => setMaxBudget(e.target.value)}
                        />
                      </BudgetInputRow>
                      <DropdownActions>
                        <DropdownButton
                          onClick={(e) => {
                            e.preventDefault();
                            setMinBudget("");
                            setMaxBudget("");
                            applyFilters({ minBudget: "", maxBudget: "" });
                            setShowBudgetDropdown(false);
                          }}
                        >
                          Clear
                        </DropdownButton>
                        <DropdownButton
                          primary
                          onClick={(e) => {
                            e.preventDefault();
                            applyFilters();
                            setShowBudgetDropdown(false);
                          }}
                        >
                          Apply
                        </DropdownButton>
                      </DropdownActions>
                    </DropdownContent>
                  </DropdownMenu>
                )}
              </FilterPill>

              {/* Category Filter */}
              <FilterPill
                ref={categoryDropdownRef}
                filled={selectedCategory}
                onClick={() => {
                  setShowCategoryDropdown(!showCategoryDropdown);
                  setShowBudgetDropdown(false);
                  setShowLocationDropdown(false);
                }}
              >
                {getCategoryText()}
                <ChevronDown size={14} />
                {showCategoryDropdown && (
                  <DropdownMenu onClick={(e) => e.stopPropagation()}>
                    <DropdownContent>
                      <DropdownLabel>Task Category</DropdownLabel>
                      <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                        <DropdownOption
                          onClick={() => {
                            setSelectedCategory("");
                            applyFilters({ selectedCategory: "" });
                            setShowCategoryDropdown(false);
                          }}
                          style={{
                            background: !selectedCategory
                              ? "#f8fafc"
                              : "transparent",
                            fontWeight: !selectedCategory ? "600" : "400",
                          }}
                        >
                          All Categories
                        </DropdownOption>
                        {categories.map((category) => (
                          <DropdownOption
                            key={category.id}
                            onClick={() => {
                              setSelectedCategory(category.id.toString());
                              applyFilters({
                                selectedCategory: category.id.toString(),
                              });
                              setShowCategoryDropdown(false);
                            }}
                            style={{
                              background:
                                selectedCategory === category.id.toString()
                                  ? "#f8fafc"
                                  : "transparent",
                              fontWeight:
                                selectedCategory === category.id.toString()
                                  ? "600"
                                  : "400",
                            }}
                          >
                            {category.name}
                          </DropdownOption>
                        ))}
                      </div>
                      <DropdownActions>
                        <DropdownButton
                          onClick={(e) => {
                            e.preventDefault();
                            setSelectedCategory("");
                            applyFilters({ selectedCategory: "" });
                            setShowCategoryDropdown(false);
                          }}
                        >
                          Clear
                        </DropdownButton>
                      </DropdownActions>
                    </DropdownContent>
                  </DropdownMenu>
                )}
              </FilterPill>

              {/* Task Type Filter */}
              <FilterPill
                filled={taskType !== "all"}
                onClick={() => {
                  /* Not used with native select */
                }}
              >
                {getTaskTypeText()}
                <select
                  value={taskType}
                  onChange={(e) => {
                    const newTaskType = e.target.value;
                    setTaskType(newTaskType);
                    applyFilters({ taskType: newTaskType });
                  }}
                  style={{
                    opacity: 0,
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    cursor: "pointer",
                  }}
                >
                  <option value="all">All tasks</option>
                  <option value="online">Remote</option>
                  <option value="in-person">In-person</option>
                </select>
                <ChevronDown size={14} />
              </FilterPill>

              {/* Sort By Filter */}
              <FilterPill
                filled={sortBy !== "date_desc"}
                onClick={() => {
                  /* Not used with native select */
                }}
              >
                {getSortByText()}
                <select
                  value={sortBy}
                  onChange={(e) => {
                    const newSortBy = e.target.value;
                    setSortBy(newSortBy);
                    applyFilters({ sortBy: newSortBy });
                  }}
                  style={{
                    opacity: 0,
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    cursor: "pointer",
                  }}
                >
                  <option value="date_desc">Most recent</option>
                  <option value="date_asc">Oldest first</option>
                  <option value="budget_desc">Highest budget</option>
                  <option value="budget_asc">Lowest budget</option>
                </select>
                <ChevronDown size={14} />
              </FilterPill>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <FilterPill
                  onClick={(e) => {
                    e.preventDefault();
                    clearFilters();
                  }}
                  danger
                >
                  <X size={14} style={{ marginRight: "4px" }} />
                  Clear
                </FilterPill>
              )}
            </FilterPillsRow>
          </SearchBarContainer>

          {/* Mobile bottom sheet */}
          {isMobile && isFiltersOpen && (
            <>
              <Backdrop onClick={() => setIsFiltersOpen(false)} />
              <MobileSheet ref={mobileSheetRef}>
                <MobileSheetHeader>
                  <h2>
                    <Filter size={20} /> Filter Tasks
                  </h2>
                  <button
                    className="close-button"
                    onClick={() => setIsFiltersOpen(false)}
                  >
                    <X size={20} />
                  </button>
                </MobileSheetHeader>

                <MobileSheetContent>
                  <MobileFilterForm onSubmit={(e) => e.preventDefault()}>
                    <MobileFilterGroup>
                      <MobileFilterLabel>
                        Search by title or description
                      </MobileFilterLabel>
                      <MobileFilterInput
                        type="text"
                        placeholder="Search tasks..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                      />
                    </MobileFilterGroup>

                    <MobileFilterGroup>
                      <MobileFilterLabel>Task Type</MobileFilterLabel>
                      <MobileFilterSelect
                        value={taskType}
                        onChange={(e) => {
                          const newTaskType = e.target.value;
                          setTaskType(newTaskType);
                        }}
                      >
                        <option value="all">All</option>
                        <option value="online">Remote</option>
                        <option value="in-person">In-person</option>
                      </MobileFilterSelect>
                    </MobileFilterGroup>

                    <MobileFilterGroup>
                      <MobileFilterLabel>Category</MobileFilterLabel>
                      <MobileFilterSelect
                        value={selectedCategory}
                        onChange={(e) => {
                          const newCategory = e.target.value;
                          setSelectedCategory(newCategory);
                        }}
                      >
                        <option value="">All Categories</option>
                        {categories.map((category) => (
                          <option
                            key={category.id}
                            value={category.id.toString()}
                          >
                            {category.name}
                          </option>
                        ))}
                      </MobileFilterSelect>
                    </MobileFilterGroup>

                    <MobileFilterGroup>
                      <MobileFilterLabel>Sort By</MobileFilterLabel>
                      <MobileFilterSelect
                        value={sortBy}
                        onChange={(e) => {
                          const newSortBy = e.target.value;
                          setSortBy(newSortBy);
                        }}
                      >
                        <option value="date_desc">Newest</option>
                        <option value="date_asc">Oldest</option>
                        <option value="budget_desc">Budget: High to Low</option>
                        <option value="budget_asc">Budget: Low to High</option>
                      </MobileFilterSelect>
                    </MobileFilterGroup>

                    <MobileFilterGroup>
                      <MobileFilterLabel>Budget ($)</MobileFilterLabel>
                      <BudgetInputRow style={{ gap: "8px" }}>
                        <MobileFilterInput
                          type="number"
                          inputMode="numeric"
                          placeholder="Min"
                          value={minBudget}
                          onChange={(e) => setMinBudget(e.target.value)}
                        />
                        <span>to</span>
                        <MobileFilterInput
                          type="number"
                          inputMode="numeric"
                          placeholder="Max"
                          value={maxBudget}
                          onChange={(e) => setMaxBudget(e.target.value)}
                        />
                      </BudgetInputRow>
                    </MobileFilterGroup>

                    <MobileFilterGroup
                      style={{
                        opacity: taskType === "online" ? 0.5 : 1,
                        pointerEvents: taskType === "online" ? "none" : "auto",
                      }}
                    >
                      <MobileFilterLabel>
                        Location{" "}
                        {taskType === "online"
                          ? "(disabled for remote tasks)"
                          : ""}
                      </MobileFilterLabel>
                      <MobileFilterInput
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength="10"
                        placeholder={
                          taskType === "online"
                            ? "Not applicable for remote tasks"
                            : "Enter ZIP code"
                        }
                        value={zipcode}
                        disabled={taskType === "online"}
                        onChange={(e) => {
                          if (taskType === "online") return;
                          setZipcode(e.target.value);
                          if (e.target.value.trim()) setUserLocation(null);
                        }}
                      />
                      <MobileButton
                        type="button"
                        onClick={
                          taskType === "online" ? undefined : getCurrentLocation
                        }
                        disabled={taskType === "online" || isGettingLocation}
                        style={{
                          marginTop: "8px",
                          cursor:
                            taskType === "online" || isGettingLocation
                              ? "not-allowed"
                              : "pointer",
                          background: userLocation
                            ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                            : undefined,
                        }}
                      >
                        <Navigation size={16} />{" "}
                        {isGettingLocation
                          ? "Getting Location..."
                          : userLocation
                          ? "Location Set ✓"
                          : "Use My Location"}
                      </MobileButton>
                      {userLocation && (
                        <div
                          style={{
                            marginTop: "8px",
                            padding: "12px 16px",
                            background:
                              "linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)",
                            border: "1px solid #a7f3d0",
                            borderRadius: "16px",
                            fontSize: "14px",
                            color: "#065f46",
                            fontWeight: "600",
                          }}
                        >
                          📍 Using your location: {userLocation.lat},{" "}
                          {userLocation.lng}
                        </div>
                      )}
                    </MobileFilterGroup>

                    <MobileFilterGroup
                      style={{
                        opacity: taskType === "online" ? 0.5 : 1,
                        pointerEvents: taskType === "online" ? "none" : "auto",
                      }}
                    >
                      <MobileFilterLabel>
                        Search Radius ({radius} miles){" "}
                        {taskType === "online"
                          ? "(disabled for remote tasks)"
                          : ""}
                      </MobileFilterLabel>
                      <input
                        type="range"
                        min="5"
                        max="100"
                        step="5"
                        value={radius}
                        disabled={taskType === "online"}
                        onChange={(e) => {
                          if (taskType === "online") return;
                          setRadius(Number(e.target.value));
                        }}
                        style={{ width: "100%" }}
                      />
                    </MobileFilterGroup>

                    <MobileSheetFooter>
                      <MobileButton onClick={clearFilters} style={{ flex: 1 }}>
                        Clear
                      </MobileButton>
                      <MobileButton
                        primary
                        onClick={applyFilters}
                        style={{ flex: 1 }}
                      >
                        Apply Filters
                      </MobileButton>
                    </MobileSheetFooter>
                  </MobileFilterForm>
                </MobileSheetContent>
              </MobileSheet>
            </>
          )}

          <Grid>
            {/* Sidebar list */}
            <Sidebar selectedTask={selectedTask}>
              <ListHeader>
                <div
                  style={{ fontWeight: 800, color: theme.colors.textHeading }}
                >
                  Tasks ({tasks.length})
                </div>
                <div
                  style={{
                    color: theme.colors.textMuted,
                    fontWeight: 600,
                    fontSize: ".9rem",
                  }}
                >
                  {isLoading ? "Loading..." : "Select to view"}
                </div>
              </ListHeader>

              <List>
                {isLoading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <div
                      key={i}
                      style={{
                        padding: "12px 16px",
                        borderRadius: 18,
                        border: `1px solid ${theme.colors.border}`,
                        background: "#fff",
                      }}
                    >
                      <Skeleton h="16px" />
                      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                        <Skeleton h="12px" style={{ flex: "0 0 80px" }} />
                        <Skeleton h="12px" style={{ flex: "0 0 100px" }} />
                      </div>
                    </div>
                  ))
                ) : tasks.length === 0 ? (
                  <Empty>
                    <div>No tasks match your filters.</div>
                    <div
                      style={{
                        fontSize: "0.9rem",
                        marginTop: "0.5rem",
                        color: theme.colors.textBody,
                      }}
                    >
                      Try adjusting your filters to expand results and find more
                      opportunities.
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "12px",
                        flexDirection:
                          window.innerWidth <= 480 ? "column" : "row",
                        alignItems: "center",
                        marginTop: "16px",
                      }}
                    >
                      <EmptyStateButton
                        onClick={() =>
                          isMobile ? setIsFiltersOpen(true) : clearFilters()
                        }
                      >
                        <Filter size={16} />
                        {isMobile ? "Adjust Filters" : "Clear Filters"}
                      </EmptyStateButton>
                    </div>
                    {userState?.user?.user_id ? (
                      // Signed in users - offer to set up detailed notification preferences
                      <NotificationBanner style={{ marginTop: "16px" }}>
                        <div className="icon">
                          <Bell size={18} />
                        </div>
                        <div className="content">
                          <p className="title">
                            Get notified when tasks are posted
                          </p>
                          <p className="description">
                            Set up custom alerts for tasks matching your
                            criteria.
                          </p>
                        </div>
                        <NotificationButton
                          onClick={() => navigate("/account?tab=preferences")}
                        >
                          <Bell size={14} />
                          Set Alerts
                        </NotificationButton>
                      </NotificationBanner>
                    ) : (
                      // Not signed in - encourage sign up for notifications
                      <>
                        <div
                          style={{
                            fontSize: "0.9rem",
                            marginTop: "1rem",
                            color: theme.colors.textBody,
                          }}
                        >
                          Sign up to get notified when new tasks are posted in
                          your area!
                        </div>
                        <EmptyStateButton onClick={() => navigate("/signup")}>
                          <User size={16} />
                          Sign Up for Notifications
                        </EmptyStateButton>
                      </>
                    )}
                  </Empty>
                ) : (
                  tasks.map((task) => (
                    <Item
                      key={task.id}
                      selected={selectedTask?.id === task.id}
                      onClick={() => setSelectedTask(task)}
                    >
                      <ItemTopRow>
                        <ItemHeader>
                          <ItemTitle style={{ textTransform: "capitalize" }}>
                            {task.title}
                          </ItemTitle>
                          <ItemSubtitle>
                            <Avatar
                              size="24px"
                              fontSize="12px"
                              bgColor={getUserColor(
                                task.poster?.id || task.creator_id
                              )}
                              style={{
                                marginRight: "8px",
                                verticalAlign: "middle",
                              }}
                            >
                              {task.poster?.avatar ? (
                                <img
                                  src={task.poster.avatar}
                                  alt="Poster"
                                  onError={(e) => {
                                    console.log(
                                      "Avatar image failed to load:",
                                      task.poster.avatar
                                    );
                                    e.target.style.display = "none";
                                    e.target.nextSibling.style.display =
                                      "block";
                                  }}
                                  onLoad={() => {
                                    console.log(
                                      "Avatar image loaded successfully:",
                                      task.poster.avatar
                                    );
                                  }}
                                  style={{ display: "block" }}
                                  referrerPolicy="no-referrer"
                                  crossOrigin="anonymous"
                                />
                              ) : null}
                              <span
                                style={{
                                  display: task.poster?.avatar
                                    ? "none"
                                    : "block",
                                }}
                              >
                                {getUserInitials(
                                  task.poster?.first_name,
                                  task.poster?.last_name
                                )}
                              </span>
                            </Avatar>
                            {task.poster?.first_name || "Anonymous"}
                          </ItemSubtitle>
                        </ItemHeader>
                        <ItemRightSection>
                          <Price>${task.budget}</Price>
                          <MobileChevron>
                            <ChevronRight size={16} />
                          </MobileChevron>
                        </ItemRightSection>
                      </ItemTopRow>

                      <ItemRow>
                        <Badge>
                          <MapPin size={14} />
                          {task.task_type === "online"
                            ? "Remote"
                            : task.task_type === "in-person"
                            ? task.location || "In-person"
                            : task.location || "Location not specified"}
                        </Badge>
                        <Badge>
                          <Clock size={14} />
                          {formatDistanceToNow(parseDate(task.created_at), {
                            addSuffix: true,
                          })}
                        </Badge>
                        {typeof task.distance_miles === "number" && (
                          <Badge>
                            <Navigation size={14} />
                            {task.distance_miles.toFixed(1)} mi
                          </Badge>
                        )}
                      </ItemRow>
                    </Item>
                  ))
                )}
              </List>
            </Sidebar>

            {/* Details */}
            {selectedTask ? (
              <Details selectedTask={selectedTask}>
                <StickyHead>
                  <div
                    style={{
                      display: window.innerWidth > 1024 ? "none" : "block",
                    }}
                  >
                    <BackButton
                      onClick={() => setSelectedTask(null)}
                      size={15}
                      title="Back to task list"
                    />
                  </div>
                  <div>
                    <Link
                      to={`/tasks/${selectedTask.id}`}
                      style={{ textTransform: "capitalize" }}
                    >
                      {selectedTask.title}
                    </Link>
                  </div>
                  <PosterRow>
                    <PosterInfo>
                      <span>
                        Posted{" "}
                        {formatDistanceToNow(
                          parseDate(selectedTask.created_at),
                          {
                            addSuffix: true,
                          }
                        )}{" "}
                        by
                      </span>
                      <Avatar
                        bgColor={getUserColor(
                          selectedTask.poster?.id || selectedTask.creator_id
                        )}
                      >
                        {selectedTask.poster?.avatar ? (
                          <img
                            src={selectedTask.poster.avatar}
                            alt="Poster avatar"
                            onError={(e) => {
                              console.log(
                                "Right pane avatar image failed to load:",
                                selectedTask.poster.avatar
                              );
                              e.target.style.display = "none";
                              e.target.nextSibling.style.display = "block";
                            }}
                            onLoad={() => {
                              console.log(
                                "Right pane avatar image loaded successfully:",
                                selectedTask.poster.avatar
                              );
                            }}
                            style={{ display: "block" }}
                            referrerPolicy="no-referrer"
                            crossOrigin="anonymous"
                          />
                        ) : null}
                        <span
                          style={{
                            display: selectedTask.poster?.avatar
                              ? "none"
                              : "block",
                          }}
                        >
                          {getUserInitials(
                            selectedTask.poster?.first_name,
                            selectedTask.poster?.last_name
                          )}
                        </span>
                      </Avatar>
                      <span
                        style={{
                          color: theme.colors.textBody,
                          textDecoration: "none",
                          transition: "text-decoration 0.2s ease",
                          fontWeight: 700,
                          cursor: "pointer",
                          textTransform: "capitalize",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.textDecoration = "underline";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.textDecoration = "none";
                        }}
                        onClick={() => {
                          window.location.href = `/users/${selectedTask.poster?.id}`;
                        }}
                      >
                        {selectedTask.poster?.first_name &&
                        selectedTask.poster?.last_name
                          ? `${selectedTask.poster.first_name} ${selectedTask.poster.last_name}`
                          : selectedTask.poster?.username || "User"}
                      </span>
                    </PosterInfo>

                    {/* Show Manage Task button if user is the poster */}
                    {userState.user &&
                      selectedTask.poster?.id === userState.user.user_id && (
                        <ManageTaskButton
                          onClick={() =>
                            navigate(`/tasks/${selectedTask.id}/manage`)
                          }
                        >
                          <Settings size={14} /> Manage Task
                        </ManageTaskButton>
                      )}
                  </PosterRow>
                </StickyHead>

                <ContentArea>
                  <StatGrid>
                    <SoftStat>
                      <div>
                        <DollarSign size={18} />
                      </div>
                      <div>
                        <div className="label">Budget</div>
                        <div className="value">${selectedTask.budget}</div>
                      </div>
                    </SoftStat>
                    <SoftStat>
                      <div>
                        <Calendar size={18} />
                      </div>
                      <div>
                        <div className="label">Deadline</div>
                        <div className="value">
                          {selectedTask.due_date
                            ? format(
                                parseDate(selectedTask.due_date),
                                "MMM d, yyyy"
                              )
                            : "N/A"}
                        </div>
                      </div>
                    </SoftStat>
                    <SoftStat>
                      <div>
                        <MapPin size={18} />
                      </div>
                      <div>
                        <div className="label">Location</div>
                        <div className="value">
                          {selectedTask.task_type === "online"
                            ? "Remote"
                            : selectedTask.task_type === "in-person"
                            ? selectedTask.location || "In-person"
                            : selectedTask.location || "Location not specified"}
                        </div>
                      </div>
                    </SoftStat>
                  </StatGrid>

                  <ButtonRow>
                    <Primary
                      onClick={() => {
                        if (!userState?.user?.user_id) {
                          navigate("/signin");
                          return;
                        }
                        setShowOfferForm(true);
                        setActiveTab("offers");
                      }}
                    >
                      <Star size={16} /> Submit an Offer
                    </Primary>
                    <Ghost
                      onClick={() => {
                        if (!userState?.user?.user_id) {
                          navigate("/signin");
                          return;
                        }
                        setShowQuestionForm(true);
                        setActiveTab("qa");
                        setShowOfferForm(false);
                      }}
                    >
                      <MessageCircle size={16} /> Ask a Question
                    </Ghost>
                  </ButtonRow>

                  {showOfferForm && (
                    <div style={{ marginTop: 14 }}>
                      <OfferFormComponent
                        task={selectedTask}
                        onCancel={() => setShowOfferForm(false)}
                        onOfferSubmitted={(o) => {
                          addNewOffer(o);
                          setShowOfferForm(false);
                        }}
                      />
                    </div>
                  )}

                  {showQuestionForm && (
                    <div style={{ marginTop: 14 }}>
                      <QuestionFormComponent
                        task={selectedTask}
                        onCancel={() => setShowQuestionForm(false)}
                        onQuestionSubmitted={(q) => {
                          addNewQuestion(q);
                          setShowQuestionForm(false);
                        }}
                      />
                    </div>
                  )}

                  <Section>
                    <SectionTitle>
                      <Briefcase size={18} /> Project Description
                    </SectionTitle>
                    <Body>{selectedTask.description}</Body>
                  </Section>

                  {selectedTask.time_windows?.length > 0 && (
                    <Section>
                      <SectionTitle>
                        <Clock size={18} /> Availability
                      </SectionTitle>
                      <TagWrap>
                        {selectedTask.time_windows.map((tw) => (
                          <TimePill key={tw.id}>
                            {tw.name}: {tw.start_time} – {tw.end_time}
                          </TimePill>
                        ))}
                      </TagWrap>
                    </Section>
                  )}

                  {selectedTask.images?.length > 0 && (
                    <Section>
                      <SectionTitle>
                        <ImageIcon size={18} /> Attachments
                      </SectionTitle>
                      <div
                        style={{ display: "flex", gap: 12, flexWrap: "wrap" }}
                      >
                        {selectedTask.images.map((img, index) => (
                          <Thumb
                            key={img.id}
                            src={img.url}
                            alt="Task Attachment"
                            onClick={() =>
                              openImageModal(selectedTask.images, index)
                            }
                          />
                        ))}
                      </div>
                    </Section>
                  )}

                  <Activity
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    offers={offers}
                    questions={questions}
                    isOffersLoading={isOffersLoading}
                    isQuestionsLoading={isQuestionsLoading}
                  />
                </ContentArea>
              </Details>
            ) : isLoading ? (
              <Details selectedTask={selectedTask}>
                <StickyHead>
                  <div>
                    <Skeleton
                      h="20px"
                      style={{ width: "20px", borderRadius: "4px" }}
                    />
                  </div>
                  <div>
                    <Skeleton
                      h="24px"
                      style={{ width: "60%", borderRadius: "6px" }}
                    />
                  </div>
                  <PosterRow>
                    <PosterInfo>
                      <Skeleton
                        h="16px"
                        style={{ width: "80px", borderRadius: "4px" }}
                      />
                      <Skeleton
                        h="32px"
                        style={{ width: "32px", borderRadius: "50%" }}
                      />
                      <Skeleton
                        h="16px"
                        style={{ width: "100px", borderRadius: "4px" }}
                      />
                    </PosterInfo>
                  </PosterRow>
                </StickyHead>

                <ContentArea>
                  <StatGrid>
                    <SoftStat>
                      <div>
                        <Skeleton
                          h="18px"
                          style={{ width: "18px", borderRadius: "4px" }}
                        />
                      </div>
                      <div>
                        <div className="label">
                          <Skeleton
                            h="14px"
                            style={{ width: "50px", borderRadius: "4px" }}
                          />
                        </div>
                        <div className="value">
                          <Skeleton
                            h="20px"
                            style={{ width: "60px", borderRadius: "4px" }}
                          />
                        </div>
                      </div>
                    </SoftStat>
                    <SoftStat>
                      <div>
                        <Skeleton
                          h="18px"
                          style={{ width: "18px", borderRadius: "4px" }}
                        />
                      </div>
                      <div>
                        <div className="label">
                          <Skeleton
                            h="14px"
                            style={{ width: "60px", borderRadius: "4px" }}
                          />
                        </div>
                        <div className="value">
                          <Skeleton
                            h="20px"
                            style={{ width: "70px", borderRadius: "4px" }}
                          />
                        </div>
                      </div>
                    </SoftStat>
                    <SoftStat>
                      <div>
                        <Skeleton
                          h="18px"
                          style={{ width: "18px", borderRadius: "4px" }}
                        />
                      </div>
                      <div>
                        <div className="label">
                          <Skeleton
                            h="14px"
                            style={{ width: "40px", borderRadius: "4px" }}
                          />
                        </div>
                        <div className="value">
                          <Skeleton
                            h="20px"
                            style={{ width: "80px", borderRadius: "4px" }}
                          />
                        </div>
                      </div>
                    </SoftStat>
                  </StatGrid>

                  <div style={{ margin: "16px 0" }}>
                    <Skeleton
                      h="18px"
                      style={{
                        width: "80px",
                        borderRadius: "4px",
                        marginBottom: "8px",
                      }}
                    />
                    <Skeleton
                      h="16px"
                      style={{
                        width: "100%",
                        borderRadius: "4px",
                        marginBottom: "4px",
                      }}
                    />
                    <Skeleton
                      h="16px"
                      style={{
                        width: "90%",
                        borderRadius: "4px",
                        marginBottom: "4px",
                      }}
                    />
                    <Skeleton
                      h="16px"
                      style={{ width: "75%", borderRadius: "4px" }}
                    />
                  </div>

                  <div
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      color: theme.colors.textMuted,
                    }}
                  >
                    Loading task details...
                  </div>
                </ContentArea>
              </Details>
            ) : tasks.length === 0 ? (
              <Details>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "400px",
                    textAlign: "center",
                    color: theme.colors.textMuted,
                    padding: "40px 20px",
                  }}
                >
                  <Search
                    size={48}
                    style={{ marginBottom: "16px", opacity: 0.5 }}
                  />
                  <h3
                    style={{
                      margin: "0 0 8px 0",
                      color: theme.colors.textHeading,
                      fontSize: "1.1rem",
                      fontWeight: 600,
                    }}
                  >
                    No tasks found
                  </h3>
                  <p
                    style={{
                      margin: "0 0 20px 0",
                      fontSize: "0.9rem",
                      maxWidth: "300px",
                      lineHeight: 1.5,
                    }}
                  >
                    Try adjusting your search filters to find more
                    opportunities.
                  </p>

                  {/* Notification banner */}
                  <NotificationBanner
                    style={{ maxWidth: "380px", margin: "0 auto" }}
                  >
                    <div className="icon">
                      <Bell size={20} />
                    </div>
                    <div className="content">
                      <p className="title">
                        Get notified when tasks are posted
                      </p>
                      <p className="description">
                        Set up alerts for tasks matching your criteria and
                        location.
                      </p>
                    </div>
                    {userState?.user?.token ? (
                      <NotificationButton
                        onClick={() => navigate("/account?tab=preferences")}
                      >
                        <Bell size={14} />
                        Set Alerts
                      </NotificationButton>
                    ) : (
                      <NotificationButton onClick={() => navigate("/signin")}>
                        <LogIn size={14} />
                        Sign In
                      </NotificationButton>
                    )}
                  </NotificationBanner>
                </div>
              </Details>
            ) : (
              <Details>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "400px",
                    textAlign: "center",
                    color: theme.colors.textMuted,
                    padding: "40px 20px",
                  }}
                >
                  <div style={{ marginBottom: "16px", opacity: 0.5 }}>
                    <Target size={48} />
                  </div>
                  <h3
                    style={{
                      margin: "0 0 8px 0",
                      color: theme.colors.textHeading,
                      fontSize: "1.1rem",
                      fontWeight: 600,
                    }}
                  >
                    Select a task to view details
                  </h3>
                  <p
                    style={{
                      margin: "0",
                      fontSize: "0.9rem",
                      maxWidth: "300px",
                      lineHeight: 1.5,
                    }}
                  >
                    Click on a task from the list to see its details,
                    requirements, and submit an offer.
                  </p>
                </div>
              </Details>
            )}
          </Grid>
        </Container>

        {/* Image Modal */}
        <ImageModal
          images={modalImages}
          currentIndex={currentImageIndex}
          isOpen={isImageModalOpen}
          onClose={closeImageModal}
          onNext={handleNextImage}
          onPrevious={handlePreviousImage}
        />
      </Page>
    </>
  );
};

const TabContainer = styled.div`
  display: flex;
  gap: 2px;
  margin: 0 0 24px;
  background: #f8fafc;
  border-radius: 12px;
  padding: 4px;
  box-shadow: inset 0 1px 3px rgba(15, 23, 42, 0.1);
  width: fit-content;
  max-width: 400px;
`;

const TabButton = styled.button`
  background: ${(props) => (props.$active ? "#ffffff" : "transparent")};
  border: none;
  border-radius: 10px;
  padding: 8px 10px;
  font-weight: 700;
  font-size: 0.9rem;
  color: ${(props) =>
    props.$active ? theme.colors.primaryText : theme.colors.textMuted};
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${(props) =>
    props.$active ? "0 2px 8px rgba(15, 23, 42, 0.1)" : "none"};
  transform: ${(props) => (props.$active ? "translateY(-1px)" : "none")};
  white-space: nowrap;

  &:hover {
    color: ${theme.colors.primaryText};
    background: ${(props) =>
      props.$active ? "#ffffff" : "rgba(255, 255, 255, 0.5)"};
  }
`;

// Activity
const Activity = ({
  activeTab,
  setActiveTab,
  offers,
  questions = [],
  isOffersLoading,
  isQuestionsLoading,
}) => {
  return (
    <Section id="activity">
      <TabContainer>
        <TabButton
          $active={activeTab === "offers"}
          onClick={() => setActiveTab("offers")}
        >
          Offers ({offers.length})
        </TabButton>
        <TabButton
          $active={activeTab === "qa"}
          onClick={() => setActiveTab("qa")}
        >
          Q & A ({questions?.length || 0})
        </TabButton>
      </TabContainer>

      {activeTab === "offers" ? (
        offers.length ? (
          offers.map((offer, i) => (
            <OfferCard key={offer.id} style={{ animationDelay: `${i * 80}ms` }}>
              <OfferHeader>
                <Avatar style={{ width: 32, height: 32 }}>
                  {offer.user?.avatar ? (
                    <img src={offer.user.avatar} alt="User avatar" />
                  ) : (
                    <User size={16} />
                  )}
                </Avatar>
                <OfferUserInfo>
                  <OfferUserName>
                    {offer.user?.first_name && offer.user?.last_name
                      ? `${offer.user.first_name} ${offer.user.last_name}`.trim()
                      : offer.user?.username || "Anonymous User"}
                    <VerificationBadge
                      isVerified={offer.user?.email_verified}
                      size="14px"
                      spacing="4px"
                    />
                  </OfferUserName>
                  <OfferTimestamp>
                    {formatDistanceToNow(parseDate(offer.created_at), {
                      addSuffix: true,
                    })}
                  </OfferTimestamp>
                </OfferUserInfo>
                <OfferPrice>${offer.offer_amount}</OfferPrice>
              </OfferHeader>

              <OfferProposal>{offer.proposal}</OfferProposal>

              {offer.previous_similar_work && (
                <OfferSection>
                  <OfferSectionTitle>Similar Work</OfferSectionTitle>
                  <OfferSectionContent>
                    {offer.previous_similar_work}
                  </OfferSectionContent>
                </OfferSection>
              )}

              {offer.years_experience && (
                <ExperienceTag style={{ marginBottom: "16px" }}>
                  {offer.years_experience} Years Experience
                </ExperienceTag>
              )}

              <OfferMeta>
                <OfferMetaItem>
                  <div className="label">Est. Timeline</div>
                  <div className="value">{offer.duration || "N/A"}</div>
                </OfferMetaItem>
              </OfferMeta>
            </OfferCard>
          ))
        ) : (
          <Empty>No offers yet — be the first to pitch!</Empty>
        )
      ) : (
        <div>
          {questions?.length ? (
            questions.map((q, i) => (
              <QuestionCard
                key={q.id}
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <QuestionHeader>
                  <Avatar style={{ width: 40, height: 40 }}>
                    {q.avatar ? (
                      <img src={q.avatar} alt="User avatar" />
                    ) : (
                      <User size={20} />
                    )}
                  </Avatar>
                  <QuestionUserInfo>
                    <QuestionUserName>
                      {q.user_name || "Anonymous User"}
                      <VerificationBadge
                        isVerified={q.email_verified}
                        size="14px"
                        spacing="4px"
                      />
                    </QuestionUserName>
                    <QuestionTimestamp>
                      {formatDistanceToNow(parseDate(q.created_at), {
                        addSuffix: true,
                      })}
                    </QuestionTimestamp>
                  </QuestionUserInfo>
                  <QuestionIcon>
                    <MessageCircle size={18} />
                  </QuestionIcon>
                </QuestionHeader>

                <QuestionText>{q.question_text || q.text}</QuestionText>

                {/* Display replies if they exist */}
                {q.replies && q.replies.length > 0 && (
                  <RepliesContainer>
                    {q.replies.map((reply) => (
                      <ReplyItem key={reply.id}>
                        <ReplyHeader>
                          <Avatar style={{ width: 24, height: 24 }}>
                            {reply.avatar ? (
                              <img src={reply.avatar} alt="Reply user avatar" />
                            ) : (
                              <User size={12} />
                            )}
                          </Avatar>
                          <ReplyUserName>
                            {reply.user_name || "Anonymous User"}
                          </ReplyUserName>
                          <ReplyTimestamp>
                            {formatDistanceToNow(parseDate(reply.created_at), {
                              addSuffix: true,
                            })}
                          </ReplyTimestamp>
                        </ReplyHeader>
                        <ReplyText>{reply.reply_text || reply.text}</ReplyText>
                      </ReplyItem>
                    ))}
                  </RepliesContainer>
                )}
              </QuestionCard>
            ))
          ) : (
            <Empty>No questions yet — be the first to ask!</Empty>
          )}
        </div>
      )}
    </Section>
  );
};

const mapStateToProps = (state) => ({ userState: state });
export default connect(mapStateToProps)(BrowseTasksPage);
